import React from 'react';
import type { LscVodCandidate, LscVodMatchSuggestion } from '../types';
import {
  CROSS_LEAGUE_CONFLICT_TITLE,
  HOME_AWAY_ALIGNMENT_LABEL,
  SCHEDULE_MATCH_STATUS_LABEL,
  WHAT_LSC_CHECKED_LABEL,
} from '../commissionerReviewCopy';
import {
  formatDetectedVodMatchup,
  noLeagueMatchSummaryMessage,
  possibleTeamMatchSummaryMessage,
  truthCrossLeagueConflictMessage,
  truthFrameVerificationLabel,
  truthHomeAwayConfidence,
  truthLeagueMatchLabel,
  truthScheduleBindingHeadline,
  truthScheduleBindingStatus,
  truthTimelineLabel,
} from '../vodTruth';

interface CandidateMatchSummaryProps {
  candidate: LscVodCandidate;
  bestSuggestion?: LscVodMatchSuggestion | null;
}

const CandidateMatchSummary: React.FC<CandidateMatchSummaryProps> = ({ candidate }) => {
  const publishView = candidate.commissioner_publish;
  const noLeagueMessage = noLeagueMatchSummaryMessage(candidate);
  const possibleTeamMessage = possibleTeamMatchSummaryMessage(candidate);
  const scheduleBindingStatus = truthScheduleBindingStatus(candidate);
  const timelineLabel = truthTimelineLabel(candidate);
  const scheduleHeadline = truthScheduleBindingHeadline(candidate);
  const crossLeagueMessage = truthCrossLeagueConflictMessage(candidate);
  const detectedMatchup = formatDetectedVodMatchup(candidate);
  const homeAwayConfidence = truthHomeAwayConfidence(candidate);

  return (
    <div className="card space-y-4" data-testid="candidate-match-summary">
      <h2 className="text-lg font-semibold text-white">Match summary</h2>

      {publishView && (
        <>
          <p className="text-sm text-secondary-100" data-testid="candidate-headline">
            {publishView.headline}
          </p>
          {publishView.warning ? (
            <p
              className="rounded-md border border-amber-400 bg-amber-950 px-3 py-2 text-sm text-amber-50"
              data-testid="candidate-gamertag-warning"
            >
              {publishView.warning}
            </p>
          ) : null}
        </>
      )}

      {!publishView && crossLeagueMessage && (
        <div data-testid="candidate-cross-league-conflict">
          <p className="text-sm font-medium text-red-200">{CROSS_LEAGUE_CONFLICT_TITLE}</p>
          <p className="text-sm text-red-200/90">{crossLeagueMessage}</p>
        </div>
      )}

      {detectedMatchup && (
        <p className="text-sm text-secondary-200" data-testid="candidate-detected-matchup">
          Detected matchup: {detectedMatchup}
        </p>
      )}

      {!publishView && scheduleBindingStatus === 'bound' && timelineLabel && (
        <p className="text-sm text-green-200/90" data-testid="candidate-schedule-timeline">
          {scheduleHeadline ?? `Matched to ${timelineLabel}`}
        </p>
      )}

      {!publishView && candidate.competition_match_id && (
        <p className="text-sm text-secondary-200" data-testid="candidate-assigned-match">
          Assigned match: {timelineLabel ?? candidate.competition_match_id}
        </p>
      )}

      {candidate.truth_checked_at && (
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-secondary-500">{WHAT_LSC_CHECKED_LABEL}</dt>
            <dd className="text-secondary-200" data-testid="candidate-frame-evidence">
              {truthFrameVerificationLabel(candidate)}
            </dd>
          </div>
          {!publishView && (
            <div>
              <dt className="text-secondary-500">{SCHEDULE_MATCH_STATUS_LABEL}</dt>
              <dd className="text-secondary-200" data-testid="candidate-league-resolution">
                {truthLeagueMatchLabel(candidate)}
              </dd>
            </div>
          )}
          {homeAwayConfidence && (
            <div>
              <dt className="text-secondary-500">{HOME_AWAY_ALIGNMENT_LABEL}</dt>
              <dd className="text-secondary-200 capitalize" data-testid="candidate-home-away-readiness">
                {homeAwayConfidence}
              </dd>
            </div>
          )}
        </dl>
      )}

      {!publishView && possibleTeamMessage && (
        <p className="text-sm text-amber-200/90" data-testid="candidate-possible-team-match-message">
          {possibleTeamMessage}
        </p>
      )}

      {!publishView && noLeagueMessage && (
        <p className="text-sm text-amber-200/90" data-testid="candidate-no-league-match-message">
          {noLeagueMessage}
        </p>
      )}
    </div>
  );
};

export default CandidateMatchSummary;
